import React, { useState, useRef, useEffect } from 'react';
import { X, Upload, Image as ImageIcon } from 'lucide-react';
import { Label } from './ui/label';
import { useLanguage } from '../contexts/LanguageContext';

interface MediaFile {
  id: string;
  file: File;
  previewUrl: string;
  uploading?: boolean;
  uploaded?: boolean;
  error?: string;
  url?: string;
  name: string;
  mimeType: string;
}

interface MediaUploadForWillProps {
  onMediaChange: (media: MediaFile[]) => void;
  existingMedia?: MediaFile[];
  maxFiles?: number;
}

const MediaUploadForWill: React.FC<MediaUploadForWillProps> = ({
  onMediaChange,
  existingMedia = [],
  maxFiles = 5
}) => {
  const [media, setMedia] = useState<MediaFile[]>(existingMedia);
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { language } = useLanguage();
  const isRTL = language === 'he';

  // Update media state when existingMedia prop changes
  useEffect(() => {
    console.log("existingMedia changed:", existingMedia);
    if (existingMedia && existingMedia.length > 0) {
      console.log("Setting media from existingMedia:", existingMedia);
      setMedia(existingMedia);
    }
  }, [existingMedia]);

  // Notify parent component when media changes
  useEffect(() => {
    onMediaChange(media);
  }, [media, onMediaChange]);

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      console.log("Files selected:", e.target.files);
      
      const newFiles = Array.from(e.target.files).map(file => {
        console.log("Processing file:", file.name, file.type, file.size);
        
        // Create a proper File object
        const fileObj = new File([file], file.name, {
          type: file.type,
          lastModified: file.lastModified
        });
        
        // Generate a reliable preview URL
        const previewUrl = URL.createObjectURL(file);
        console.log(`Created preview URL: ${previewUrl} for file: ${file.name}`);
        
        return {
          id: crypto.randomUUID(),
          file: fileObj,
          previewUrl: previewUrl,
          uploading: false,
          uploaded: false,
          name: file.name,
          mimeType: file.type
        };
      });
      
      const updatedMedia = [...media, ...newFiles].slice(0, maxFiles);
      console.log("Updated media after file selection:", updatedMedia);
      setMedia(updatedMedia);
      
      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const removeMedia = (id: string) => {
    setMedia(prev => {
      const updated = prev.filter(item => item.id !== id);
      // Revoke object URLs to prevent memory leaks
      const removed = prev.find(item => item.id === id);
      if (removed && removed.previewUrl) {
        URL.revokeObjectURL(removed.previewUrl);
      }
      return updated;
    });
  };

  const handleDragEnter = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      console.log("Files dropped:", e.dataTransfer.files);
      
      const newFiles = Array.from(e.dataTransfer.files)
        .filter(file => file.type.startsWith('image/'))
        .map(file => {
          console.log("Processing dropped file:", file.name, file.type, file.size);
          
          // Create a proper File object
          const fileObj = new File([file], file.name, {
            type: file.type,
            lastModified: file.lastModified
          });
          
          // Generate a reliable preview URL
          const previewUrl = URL.createObjectURL(file);
          console.log(`Created preview URL: ${previewUrl} for file: ${file.name}`);
          
          return {
            id: crypto.randomUUID(),
            file: fileObj,
            previewUrl: previewUrl,
            uploading: false,
            uploaded: false,
            name: file.name,
            mimeType: file.type
          };
        });
      
      if (newFiles.length > 0) {
        const updatedMedia = [...media, ...newFiles].slice(0, maxFiles);
        console.log("Updated media after drop:", updatedMedia);
        setMedia(updatedMedia);
      }
    }
  };

  const getImageUrl = (url: string) => {
    // Add cache-busting parameter to prevent browser caching
    const cacheBuster = `cache=${Date.now()}`;
    if (url.includes('?')) {
      return `${url}&${cacheBuster}`;
    }
    return `${url}?${cacheBuster}`;
  };

  // Use a local fallback image instead of relying on an external service
  const fallbackImageSrc = "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='100' height='100' viewBox='0 0 100 100'%3E%3Crect width='100' height='100' fill='%23f0f0f0'/%3E%3Cpath d='M30 40 L50 60 L70 40' stroke='%23999' stroke-width='4' fill='none'/%3E%3Cpath d='M30 60 L70 60' stroke='%23999' stroke-width='4' fill='none'/%3E%3C/svg%3E";

  return (
    <div className="space-y-2">
      <Label>{isRTL ? "תמונות (אופציונלי)" : "Media Attachments (Optional)"}</Label>
      
      <div 
        className={`border-2 border-dashed rounded-lg p-4 transition-colors ${
          isDragging ? 'border-blue-400 bg-blue-50' : 'border-gray-200'
        }`}
        onDragEnter={handleDragEnter}
        onDragLeave={handleDragLeave}
        onDragOver={handleDragOver}
        onDrop={handleDrop}
      >
        <div className="flex flex-wrap gap-3 mb-3">
          {media.map(item => {
            console.log("Rendering media item:", item);
            return (
              <div key={item.id} className="relative group">
                <div className="w-24 h-24 rounded-lg overflow-hidden border border-gray-200 bg-gray-50">
                  {item.previewUrl ? (
                    <div className="w-full h-full relative">
                      <img 
                        src={getImageUrl(item.previewUrl)} 
                        alt={item.name || "Preview"} 
                        className="w-full h-full object-cover"
                        onError={(e) => {
                          console.log(`Error loading image: ${item.previewUrl}`);
                          
                          // Try with a different URL format if the original fails
                          if (item.url && item.url !== item.previewUrl) {
                            console.log(`Falling back to alternate URL: ${item.url}`);
                            e.currentTarget.src = getImageUrl(item.url);
                          } else if (item.file && !item.uploaded) {
                            // For new files that haven't been uploaded yet, try to create a new object URL
                            console.log('Trying to create a new object URL from the file');
                            try {
                              const newUrl = URL.createObjectURL(item.file);
                              console.log(`Created new object URL: ${newUrl}`);
                              e.currentTarget.src = newUrl;
                            } catch (err) {
                              console.error('Failed to create object URL:', err);
                              e.currentTarget.src = fallbackImageSrc;
                              e.currentTarget.onerror = null;
                            }
                          } else {
                            console.log('Using embedded SVG fallback image');
                            e.currentTarget.src = fallbackImageSrc;
                            e.currentTarget.onerror = null; // Prevent infinite error loop
                          }
                        }}
                      />
                      {/* Add a loading indicator for new files */}
                      {!item.uploaded && !item.error && (
                        <div className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-20">
                          <div className="text-xs text-white bg-black bg-opacity-50 px-2 py-1 rounded">
                            Preview
                          </div>
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="w-full h-full flex items-center justify-center bg-gray-100">
                      <ImageIcon className="h-8 w-8 text-gray-400" />
                    </div>
                  )}
                  {item.uploaded && (
                    <div className="absolute top-0 right-0 bg-green-500 text-white text-xs px-1 rounded-bl">
                      ✓
                    </div>
                  )}
                </div>
                <button
                  type="button"
                  onClick={() => removeMedia(item.id)}
                  className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 w-5 h-5 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                >
                  <X className="w-3 h-3" />
                </button>
              </div>
            );
          })}
          
          {media.length < maxFiles && (
            <div className="w-24 h-24 rounded-lg border-2 border-dashed border-gray-200 flex flex-col items-center justify-center cursor-pointer hover:bg-gray-50 transition-colors"
                 onClick={() => fileInputRef.current?.click()}>
              <Upload className="h-6 w-6 text-gray-400 mb-1" />
              <span className="text-xs text-gray-500">{isRTL ? "העלה" : "Upload"}</span>
            </div>
          )}
        </div>
        
        <input
          type="file"
          ref={fileInputRef}
          onChange={handleFileSelect}
          className="hidden"
          multiple
          accept="image/*"
        />
        
        <p className="text-xs text-center text-gray-500">
          {isRTL 
            ? `גרור ושחרר תמונות כאן או לחץ להעלאה (מקסימום ${maxFiles} קבצים)`
            : `Drag and drop images here or click to upload (max ${maxFiles} files)`
          }
        </p>
      </div>
    </div>
  );
};

export default MediaUploadForWill;
